// Página "Todas las plantillas" — destino del CTA "Ver todas las
// plantillas" que aparece en la home de Interfaz V2 en desktop.
//
// Layout:
//   • Arriba: row horizontal de chips (uno por categoría / colección),
//     scrolleable y single-select. Sin ninguno activo se muestran todas
//     las plantillas, y tocar el chip activo vuelve a ese estado
//     (ADR-2026-08-03-filtros-plantillas-sin-chip-todas).
//     Se entra sin filtro, salvo que la URL traiga `?categoria=` — que es
//     con lo que llega el "Ver más" de una fila de la home
//     (ADR-2026-08-03-ver-mas-de-una-fila-filtra-plantillas).
//   • Abajo: grilla tipo masonry (CSS columns) con tarjetas de alto
//     variable según el aspect natural de la imagen del preset. Cada
//     card tiene la imagen + overlay con título abajo a la izquierda.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  getAppScrollY,
  setAppScrollY,
} from '../../utils/appScroll';
import { useSidebar } from '../../context/SidebarContext';
import { useShellEscritorio } from '../../hooks/useShellEscritorio';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { rubroVePlantillas } from '../../lib/rubro';
import type { CollectionV2, PresetV2 } from './mockData';
import { fetchTemplatesV2 } from './templatesApi';
import { EncabezadoPantalla } from '../../components/PantallaChrome';
import {
  APARECE,
  BLOQUE_ESQUELETO,
  EsqueletoSeccion,
} from '../../components/EsqueletoSeccion';
import { fetchAllPoses } from './posesApi';
import { buildVistasSoloProductoPresets } from './vistasCatalogo';
import { ASPECT_CYCLE, MasonryCard } from './components/TemplateMasonry';

// Rubros que ven la categoría virtual "E-commerce" (poses como cards).
// Mismo set que `DESIGN_RUBROS` en InterfazV2 — duplicado para no crear
// una dependencia circular del archivo de la home.
const DESIGN_RUBROS = new Set([
  'Moda e Indumentaria',
  'Belleza y Cuidado Personal',
]);

// Orden custom del row de E-commerce: cuerpo_completo + plano_medio
// primero, detalle_close-up al final, resto en el medio.
const ecommerceCategoryRank = (cat: string | null): number => {
  if (cat === 'cuerpo_completo') return 0;
  if (cat === 'plano_medio') return 1;
  if (cat === 'detalle_close-up') return 3;
  return 2;
};

// State persistente entre mount/unmount — al volver desde un preset
// queremos ver la grilla al toque (sin skeleton) y restaurar el scroll
// + la categoría activa para que se sienta como una pantalla
// "siempre montada".
const PLANTILLAS_CACHE_KEY = 'atlas-v2-plantillas-cache';
const PLANTILLAS_SCROLL_KEY = 'atlas-v2-plantillas-scroll';

/**
 * Categoría activa, en la URL — ADR-2026-08-03-ver-mas-de-una-fila-filtra-plantillas.
 *
 * Es el filtro con el que se entra desde el "Ver más" de una fila de la home, y a la
 * vez lo que restaura el filtro al volver de un preset (el back del browser recompone
 * la URL). Sin parámetro, ningún chip activo: es el estado de una entrada limpia.
 *
 * Antes esto vivía en `sessionStorage`, y por eso la pantalla se abría con el último
 * filtro usado en toda la sesión aunque se entrara por la sidebar.
 */
const PARAM_CATEGORIA = 'categoria';

/** Estado "sin filtro": se ven todas las plantillas y ningún chip queda activo. */
const SIN_FILTRO = 'all';

interface FlatPreset extends PresetV2 {
  /** id de la categoría de origen — sirve para pasar `collectionId` al
   *  abrir el sheet en /interfaz-v2 (mismo patrón que ColeccionDetalle). */
  collectionId: string;
  collectionTitle: string;
}

const InterfazV2Plantillas: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarWidth } = useSidebar();

  // Shell que la monta (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell): `/interfaz-v2/plantillas` va en `DashboardLayout`
  // y `/plantillas` en el shell de `/inicio`. La home de V2 —a donde llevan los
  // presets y a donde cae el back sin historial— tiene una ruta distinta en cada uno.
  const rutaHomeV2 = location.pathname === '/plantillas' ? '/generar' : '/interfaz-v2';

  /**
   * Plantillas es sólo para Moda (ADR-2026-08-03-plantillas-solo-para-moda). Los
   * accesos ya no se muestran, pero la ruta sigue existiendo —un link viejo, un
   * favorito, el back del navegador—, así que también se cierra acá.
   *
   * `useBrandProfile` y no la consulta de rubro que esta pantalla ya hace más abajo:
   * ésa lee el rubro de la EMPRESA y lo que manda es el de la marca ACTIVA, que es
   * el mismo criterio que usan la sidebar y la home.
   *
   * `!cargandoMarca` es la condición que hace que esto NO sea un rebote: mientras el
   * hook carga, `activeBrand` puede ser `null` y el `??` cae al rubro de empresa, que
   * es de otra marca. Con empresa Belleza + marca activa Moda eso redirigía a la home
   * a alguien que sí tiene plantillas (ADR-2026-08-04-cargando-tambien-cubre-las-marcas).
   * No sacar la condición ni cambiarla por un chequeo de `activeBrand`.
   */
  const { activeBrand, profile: perfilEmpresa, loading: cargandoMarca } =
    useBrandProfile();
  const sinPlantillas =
    !cargandoMarca &&
    !rubroVePlantillas(activeBrand?.rubro ?? perfilEmpresa?.rubro ?? null);

  // Initializer lee el cache: si el user ya visitó esta página antes
  // en la sesión, render instantáneo sin skeleton.
  const [collections, setCollections] = useState<CollectionV2[] | null>(() => {
    try {
      const raw = sessionStorage.getItem(PLANTILLAS_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed as CollectionV2[];
      }
    } catch {
      /* noop */
    }
    return null;
  });
  // Single-select. El estado manda; la URL es su espejo (ver el effect de abajo) y
  // su semilla: entrando desde el "Ver más" de una fila de la home viene con
  // `?categoria=`, y entrando de cualquier otra forma —sidebar, "Ver todo", refresh—
  // no viene con nada y no arranca ningún chip activo.
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
    () => new URLSearchParams(location.search).get(PARAM_CATEGORIA) || SIN_FILTRO,
  );

  // Estado → URL, siempre en `replace`: cambiar de chip no es una pantalla nueva, y
  // con `push` el back tendría que deshacer un filtro por vez antes de salir. Lo que
  // esto compra es que la URL de vuelta —la que el browser recompone al cerrar un
  // preset— ya traiga el filtro que estaba puesto.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const enLaUrl = params.get(PARAM_CATEGORIA);
    if (selectedCategoryId === SIN_FILTRO) {
      if (enLaUrl === null) return;
      params.delete(PARAM_CATEGORIA);
    } else {
      if (enLaUrl === selectedCategoryId) return;
      params.set(PARAM_CATEGORIA, selectedCategoryId);
    }
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  }, [selectedCategoryId, location.search, location.pathname, navigate]);

  // Sticky fake-out: `position: sticky` no funciona acá porque el
  // `<main>` de `DashboardLayout` tiene `overflow-x-hidden` (CSS lo
  // computa a `overflow-y: auto`) y eso lo vuelve el "scroll container"
  // ancestral del sticky — pero MAIN no scrollea de verdad (es body
  // el que scrollea), así que el sticky queda congelado. Workaround:
  // observamos un sentinel arriba del row de chips; cuando sale por
  // arriba del viewport, swapeamos el chip-bar a `position: fixed`.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const chipsBarRef = useRef<HTMLDivElement | null>(null);
  const [isStuck, setIsStuck] = useState(false);
  const [chipsHeight, setChipsHeight] = useState(0);
  // La barra pegada tiene que saber en QUÉ shell está: el de escritorio (sidebar en
  // flujo, barra de créditos arriba) o el de mobile (header flotante, sin sidebar).
  // La frontera es 768 —`useShellEscritorio`, ADR-2026-08-05-el-shell-nuevo-arranca-en-768— y
  // no los 640 que preguntaba antes: entre 640 y 767 el shell ya es el de mobile
  // (no hay sidebar) pero la barra se corría `sidebarWidth` a la derecha, o sea que
  // el filtro pegado arrancaba fuera de la pantalla.
  const enEscritorio = useShellEscritorio();
  const isMobileView = !enEscritorio;

  /**
   * Dónde termina el chrome fijo de arriba, en px. Es el `top` al que se pega la
   * barra y —lo importante— la línea contra la que hay que medir el enganche: si el
   * observer sigue mirando el tope de la ventana, la barra se fija recién cuando el
   * sentinel llegó a 0 y aparece de golpe unos px más abajo, con un salto.
   *
   * En escritorio sale de `--shell-barra-creditos`, que publica el shell y vale 0
   * cuando la barra de créditos no está; se lee por herencia desde la propia barra
   * de chips. En mobile es el header flotante de `DashboardLayout`: 3.5rem. El
   * `env(safe-area-inset-top)` que ese header suma en PWA no entra en la cuenta —
   * el `top` de la barra sí lo lleva (va por CSS), así que a lo sumo el enganche se
   * adelanta esos px, que no se ven.
   */
  const [topFijo, setTopFijo] = useState(0);
  useEffect(() => {
    const medir = () => {
      if (isMobileView) {
        setTopFijo(56);
        return;
      }
      const ref = chipsBarRef.current;
      if (!ref) return;
      const valor = parseFloat(
        getComputedStyle(ref).getPropertyValue('--shell-barra-creditos'),
      );
      setTopFijo(Number.isFinite(valor) ? valor : 0);
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
    // `isStuck` en las deps: la barra de créditos aparece y desaparece (saldo, plan,
    // descarte) y su alto cambia; re-medir en cada cambio de estado alcanza para que
    // el `top` y el enganche sigan coincidiendo sin observar el DOM del shell.
  }, [collections, isMobileView, isStuck]);

  /**
   * Dónde empieza y dónde termina la COLUMNA de contenido, en coordenadas de la
   * ventana. Suelta, la barra vive dentro de esa columna y queda alineada con la
   * grilla sola; pegada se va del flujo y tenía que reconstruirla a mano
   * (`max-w-content mx-auto` + `px-5 sm:px-8`), que no es la misma cuenta que hace el
   * shell (`max-w-[90%]` + 32px de sangría): el primer chip caía a la izquierda de la
   * primera imagen.
   *
   * En vez de repetir la fórmula —que además cambia con el shell y con la sidebar
   * colapsada—, se MIDE la columna real y la barra pegada se calza contra ella. El
   * `ResizeObserver` cubre el colapso de la sidebar, que la mueve sin que haya
   * `resize` de ventana.
   */
  const columnaRef = useRef<HTMLDivElement | null>(null);
  const [columna, setColumna] = useState<{
    izquierda: number;
    derecha: number;
    ancho: number;
  } | null>(null);
  useEffect(() => {
    const el = columnaRef.current;
    if (!el) return;
    const medir = () => {
      const r = el.getBoundingClientRect();
      const cs = getComputedStyle(el);
      setColumna({
        // Borde del CONTENIDO, no de la caja: la columna trae su propio padding
        // lateral fuera del shell, y es contra el contenido que se alinea la grilla.
        izquierda: r.left + (parseFloat(cs.paddingLeft) || 0),
        derecha: r.right - (parseFloat(cs.paddingRight) || 0),
        ancho: window.innerWidth,
      });
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    window.addEventListener('resize', medir);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, [collections]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Stuck cuando el sentinel cruzó hacia arriba la línea donde termina el
        // chrome fijo (no intersecta el viewport RECORTADO por `rootMargin` y su
        // top quedó por encima de esa línea).
        setIsStuck(
          !entry.isIntersecting && entry.boundingClientRect.top < topFijo,
        );
      },
      { threshold: 0, rootMargin: `-${topFijo}px 0px 0px 0px` },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [collections, topFijo]);
  // Medimos la altura del chip-bar para meter un spacer del mismo
  // tamaño en el flow cuando se fija — evita que el grid salte.
  useEffect(() => {
    if (!chipsBarRef.current) return;
    setChipsHeight(chipsBarRef.current.offsetHeight);
  }, [collections, isStuck]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        let rubro: string | null = null;
        let companyId: string | null = null;
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('id_company')
            .eq('id', user.id)
            .single();
          const idCompany = (profile as any)?.id_company as string | undefined;
          if (idCompany) {
            companyId = idCompany;
            const { data: company } = await supabase
              .from('company_profiles')
              .select('rubro')
              .eq('id_company', idCompany)
              .single();
            rubro = (company as any)?.rubro || null;
          }
        }
        const list = await fetchTemplatesV2({ rubro, companyId });
        // Si el rubro califica, también fetcheamos las poses y armamos
        // la colección virtual "E-commerce" para mostrarla acá (sino
        // queda solo accesible desde el mobile home).
        const wantsEcommerce = rubro ? DESIGN_RUBROS.has(rubro) : false;
        let withEcommerce = list;
        if (wantsEcommerce) {
          try {
            const poses = await fetchAllPoses();
            if (poses.length > 0) {
              const ecommerce: CollectionV2 = {
                id: 'e-commerce',
                title: 'Fondo blanco',
                // Misma descripción que en la home (ADR-2026-08-03-descripciones-para-las-categorias-sin-una):
                // es la misma colección vista desde otra pantalla.
                subtitle:
                  'Tu producto sobre fondo blanco, en distintas poses y planos, listo para el e-commerce.',
                presets: [
                  ...[...poses]
                    .sort(
                      (a, b) =>
                        ecommerceCategoryRank(a.categoria) -
                        ecommerceCategoryRank(b.categoria),
                    )
                    .map(
                      (p): PresetV2 => ({
                        id: `pose-${p.id}`,
                        title: p.nombre,
                        imageUrl: p.imageUrl,
                        imageUrlFull: p.imageUrl,
                        kind: 'design' as const,
                      }),
                    ),
                  // Vistas "solo producto" (mismo catálogo que Catálogo
                  // IA) al final — cards kind 'design-producto'.
                  ...buildVistasSoloProductoPresets(),
                ],
              };
              withEcommerce = [ecommerce, ...list];
            }
          } catch {
            // silent — si las poses fallan seguimos con la lista normal
          }
        }
        if (alive) {
          setCollections(withEcommerce);
          try {
            sessionStorage.setItem(
              PLANTILLAS_CACHE_KEY,
              JSON.stringify(withEcommerce),
            );
          } catch {
            /* noop */
          }
        }
      } catch {
        // Si falla el fetch pero tenemos cache previo, conservalo.
        if (alive) setCollections((prev) => prev ?? []);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Restore scroll al volver desde un preset. Si no hay scroll guardado,
  // no hacemos nada — el browser preservará su propio scroll en
  // first-mount fresh, o caerá a top si es navegación normal.
  // Vía utils/appScroll: en desktop el scroller es el panel
  // `.content-panel-clip`, no el window (window.scrollY da siempre 0);
  // con window.scrollTo el restore era un no-op en desktop.
  useEffect(() => {
    let cancelled = false;
    let raf = 0;
    try {
      const sy = sessionStorage.getItem(PLANTILLAS_SCROLL_KEY);
      if (!sy) return;
      const y = Number(sy);
      sessionStorage.removeItem(PLANTILLAS_SCROLL_KEY);
      if (!Number.isFinite(y) || y <= 0) return;
      // La grilla (con imágenes) puede tardar VARIOS frames en alcanzar su altura
      // final. Un doble-rAF fijo a veces dispara antes → el scroll queda clampeado
      // al tope (la página todavía no es tan alta como para llegar a `y`) → el user
      // termina arriba. En vez de un timing fijo, reintentamos por frame hasta que
      // el scroll efectivamente "pega" en `y` (±2px) o hasta ~800ms. Apenas llega,
      // corta (no pelea con el scroll del user). Vía appScroll (set/getAppScrollY):
      // en desktop el scroller es el panel `.content-panel-clip`, no el window —
      // con window.scrollTo el restore era un no-op en desktop.
      const start = Date.now();
      const step = () => {
        if (cancelled) return;
        setAppScrollY(y);
        const reached = Math.abs(getAppScrollY() - y) <= 2;
        if (!reached && Date.now() - start < 800) {
          raf = requestAnimationFrame(step);
        }
      };
      raf = requestAnimationFrame(step);
    } catch {
      /* noop */
    }
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Categorías reales (excluimos la "design-row" virtual si vino — esta
  // página solo muestra plantillas tradicionales, no los modos).
  const categories = useMemo(() => {
    const real = (collections || []).filter((c) => c.id !== 'design-row');
    // Pedido del jefe: "Alfombra roja" va al FINAL de las pills (no le
    // convence). La movemos al fondo manteniendo el orden del resto.
    const isAlfombraRoja = (title?: string) =>
      (title || '').trim().toLowerCase() === 'alfombra roja';
    return [
      ...real.filter((c) => !isAlfombraRoja(c.title)),
      ...real.filter((c) => isAlfombraRoja(c.title)),
    ];
  }, [collections]);

  // Plantilla flat: cada preset con su collectionId/Title de origen. Si
  // un preset apareciera en varias categorías la dedupeamos por preset.id
  // dándole la primera categoría que lo contiene.
  const allPresets = useMemo<FlatPreset[]>(() => {
    const seen = new Set<string>();
    const out: FlatPreset[] = [];
    for (const c of categories) {
      for (const p of c.presets) {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        out.push({ ...p, collectionId: c.id, collectionTitle: c.title });
      }
    }
    return out;
  }, [categories]);

  const filteredPresets = useMemo(() => {
    if (selectedCategoryId === SIN_FILTRO) return allPresets;
    return allPresets.filter((p) => p.collectionId === selectedCategoryId);
  }, [allPresets, selectedCategoryId]);

  /**
   * Una categoría que no existe se cae a "sin filtro" en vez de dejar la grilla
   * vacía: la URL la puede escribir cualquiera (un link viejo, una colección que se
   * dio de baja, o la virtual "Fondo blanco" —que sólo se arma para ciertos rubros—
   * compartida entre cuentas). Espera a que carguen las colecciones: con
   * `collections === null` todavía no se sabe si la categoría existe.
   */
  useEffect(() => {
    if (collections === null || selectedCategoryId === SIN_FILTRO) return;
    if (categories.some((c) => c.id === selectedCategoryId)) return;
    setSelectedCategoryId(SIN_FILTRO);
  }, [collections, categories, selectedCategoryId]);

  const openPreset = (p: FlatPreset) => {
    // Guardamos scroll para restaurar al volver — sin esto la página
    // se monta de nuevo al hacer back y arranca scrolleada hasta arriba.
    // `getAppScrollY` y no `window.scrollY`: en desktop scrollea el
    // panel `.content-panel-clip` y window.scrollY da siempre 0.
    try {
      sessionStorage.setItem(PLANTILLAS_SCROLL_KEY, String(getAppScrollY()));
    } catch {
      /* noop */
    }
    // Buscamos la collection real para pasar sus presets como
    // previewList — así el sheet puede swipear entre todos.
    const sourceCollection = (collections ?? []).find(
      (c) => c.id === p.collectionId,
    );
    navigate(`${rutaHomeV2}?preset=${p.id}`, {
      state: {
        presetId: p.id,
        collectionId: p.collectionId,
        // Data sincrónica para que InterfazV2 abra el sheet en el
        // primer paint sin un flash blanco mientras carga el fetch.
        presetData: p,
        collectionPresets: sourceCollection?.presets ?? [p],
        // Con qué media arranca el preview —
        // ADR-2026-08-04-plantillas-es-una-opcion-de-generar. Se entra acá desde las
        // dos familias del sheet de Generar, y `?media=video` es cómo la de Videos
        // dice que lo que se viene a hacer es un video: el toggle del preview abre en
        // Video en vez de en Imagen. Sin el parámetro no viaja nada y el toggle queda
        // en su default de siempre.
        ...(new URLSearchParams(location.search).get('media') === 'video'
          ? { mediaInicial: 'video' as const }
          : null),
      },
    });
  };

  // Al cambiar de categoría volvemos suavemente al tope del scroll —
  // así el user ve el inicio de la grilla filtrada sin tener que
  // scrollear hacia arriba a mano. `setAppScrollY` cubre los dos
  // scrollers de la app (body en mobile, panel fijo en desktop).
  /**
   * Tocar un chip lo activa; tocar el ACTIVO lo apaga y vuelve a mostrarse todo —
   * ADR-2026-08-03-filtros-plantillas-sin-chip-todas.
   *
   * `SIN_FILTRO` sigue siendo el estado "sin filtro", pero ya no tiene chip propio:
   * es a donde se cae al deseleccionar, y con lo que se entra. Antes, con el chip
   * activo tocado de nuevo no pasaba nada y la única forma de volver a ver todo era
   * buscar "Todas las plantillas" al principio de una fila que scrollea.
   */
  const selectCategory = (id: string) => {
    setSelectedCategoryId((actual) => (actual === id ? SIN_FILTRO : id));
    setAppScrollY(0, 'smooth');
  };

  // ── Flechas de scroll del row de categorías (solo desktop) ──────────
  // En desktop el row de chips puede no entrar en el ancho disponible.
  // Mostramos una flecha en cada borde que tenga más contenido para
  // scrollear (izquierda si ya se scrolleó, derecha si falta). En mobile
  // el user swipea, así que van ocultas (`hidden sm:flex`).
  const categoryScrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const measureCategoryArrows = useCallback(() => {
    const el = categoryScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);
  useEffect(() => {
    measureCategoryArrows();
    // Re-medimos tras el paint por si el ancho de los chips cambió con
    // el font-load (evita una flecha derecha que falta al primer render).
    const raf = requestAnimationFrame(measureCategoryArrows);
    window.addEventListener('resize', measureCategoryArrows);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measureCategoryArrows);
    };
  }, [measureCategoryArrows, categories, isStuck]);
  const scrollCategories = (dir: -1 | 1) => {
    const el = categoryScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.75, behavior: 'smooth' });
  };

  // "Volver" respeta el origen: si llegamos a Plantillas por una navegación
  // dentro de la app (desde Modos, la home V2, etc.), hacemos un POP de
  // history para volver a ESA pantalla. Antes se hardcodeaba `/interfaz-v2`,
  // así que viniendo desde /modos siempre caías en la home. Si esta es la
  // primera entrada del router (deep-link o refresh directo en /plantillas),
  // `location.key === 'default'` → caemos a la home como fallback seguro.
  const goBack = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate(rutaHomeV2);
  };

  // Lienzo opaco de mobile. Va en `bg-surface-mobile` (`--app-mobile-bg`, #F9F9F9) y
  // NO en blanco: el shell de mobile pinta ESE gris, así que el blanco se veía como
  // una banda más clara en la franja que este overlay tapa y la pantalla no —los 68px
  // del header flotante, arriba del título—. El blanco era correcto cuando el lienzo
  // de mobile era blanco; desde el shell nuevo no lo es.
  const MobileOverlay = (
    <div
      className="fixed inset-0 bg-surface-mobile pointer-events-none sm:hidden"
      aria-hidden="true"
    />
  );

  // Rubro sin plantillas: a la home, sin dejar rastro en el historial (`replace`),
  // para que el back no rebote entre las dos pantallas
  // (ADR-2026-08-03-plantillas-solo-para-moda).
  if (sinPlantillas) return <Navigate to={rutaHomeV2} replace />;

  // Mientras no están las colecciones, el esqueleto — antes acá iba un `<div>` vacío,
  // o sea la pantalla en blanco hasta que aparecía todo de golpe
  // (ADR-2026-08-03-todas-las-secciones-cargan-igual). El `MobileOverlay` sigue
  // afuera: es el visor de un preset abierto y no depende de esta carga.
  if (collections === null) {
    return (
      <>
        {MobileOverlay}
        <div className="relative min-h-[100dvh] bg-surface-mobile sm:bg-surface-content lg:bg-transparent">
          {/* Las clases son las mismas que las del contenido real de abajo: el
              contenedor de la columna, el `<header className="mb-5 sm:mb-6">` —que es
              un BLOQUE, con el breadcrumb arriba del título, y no la fila de dos
              columnas de las otras secciones— y la barra de chips. */}
          <EsqueletoSeccion
            className="max-w-content mx-auto px-5 sm:px-8 pt-4 sm:pt-8 pb-24 lg:px-0 lg:pt-0"
            claseFilaTitulo="mb-5 sm:mb-6"
            anchoTitulo="w-[160px]"
            antesDelTitulo={
              // El breadcrumb — `hidden lg:inline-flex mb-4 text-[13px]`, las mismas
              // clases que el real, que sólo existe dentro del shell nuevo.
              //
              // Las dos rarezas de acá son lo que hace que coincida al píxel, y las
              // dos salieron de medir:
              //  • `inline-flex` y no `block`: al ser de nivel inline arma una LÍNEA,
              //    y el medio-interlineado del contenedor le suma ~4px arriba. Con
              //    `block` todo el esqueleto quedaba 4px más alto.
              //  • el `&nbsp;` y el `text-[13px]` en vez de un `h-5`: un `inline-flex`
              //    VACÍO no tiene línea base y se apoya entero arriba de la del
              //    renglón, lo que empujaba el título 3px para abajo. Con una línea de
              //    texto adentro toma su línea base —la misma que el breadcrumb real,
              //    que también es texto de 13px— y de paso el alto sale solo.
              <span
                className={`hidden lg:inline-flex mb-4 w-[150px] text-[13px] ${BLOQUE_ESQUELETO}`}
              >
                &nbsp;
              </span>
            }
          >
            {/* Fila de chips: mismo alto, mismo `gap-2` y el mismo aire de arriba y
                abajo (`py-3 sm:py-4` adentro, `mb-3 sm:mb-4` en la barra). */}
            <div className="relative mb-3 sm:mb-4">
              <div className="flex gap-2 overflow-hidden py-3 sm:py-4">
                {[110, 96, 130, 88, 120, 104].map((ancho, i) => (
                  <div
                    key={i}
                    // 39px: el chip real es `px-4 py-2` con texto de 14px y borde de
                    // 1px por lado. Medido.
                    className={`h-[39px] shrink-0 rounded-full bg-black/[0.06] animate-pulse`}
                    style={{ width: ancho }}
                  />
                ))}
              </div>
            </div>
            {/* Masonry: las mismas columnas y el mismo gap que la grilla real. Los
                altos siguen el ciclo de aspectos que usa la grilla, así el bloque
                gris no se lee como una cuadrícula perfecta que después se rompe. */}
            <div className="columns-2 sm:columns-3 lg:columns-4 gap-3 sm:gap-4">
              {[4 / 5, 1, 3 / 4, 4 / 5, 1, 3 / 4, 4 / 5, 1].map((aspecto, i) => (
                <div
                  key={i}
                  className="mb-3 sm:mb-4 break-inside-avoid rounded-2xl bg-black/[0.06] animate-pulse"
                  style={{ aspectRatio: String(aspecto) }}
                />
              ))}
            </div>
          </EsqueletoSeccion>
        </div>
      </>
    );
  }

  return (
    <>
      {MobileOverlay}
      {/* `lg:` acá significa "dentro del shell nuevo": esta ruta se monta en él a
          partir de 1024px y en `DashboardLayout` por debajo, con el mismo
          breakpoint. Todo lo que sigue marcado con `lg:` es alinear esta pantalla
          con `/inicio` — ADR-2026-08-03-titulos-secciones-igual-que-inicio. */}
      {/* El lienzo lo pone el shell (gris `#F9F9F9`); este casi-blanco encima lo
          dejaba como la única sección con página clara.
          `APARECE`: entra con la misma transición corta que las otras secciones al
          salir del esqueleto (ADR-2026-08-03-todas-las-secciones-cargan-igual). Acá
          no hace falta el `hidden` de `claseContenido` porque el esqueleto es un
          `return` temprano: cuando llegan las colecciones esto se monta de cero y la
          animación corre sola. */}
      <div
        className={`relative min-h-[100dvh] bg-surface-mobile sm:bg-surface-content lg:bg-transparent ${APARECE}`}
      >
        {/* Columna y aire de arriba: los pone el shell (`max-w-[90%]` y los 40px
            de la regla del título). Con los propios, el título quedaba 32px más a
            la derecha y 32px más abajo que el de `/inicio`. */}
        <div
          ref={columnaRef}
          className="max-w-content mx-auto px-5 sm:px-8 pt-4 sm:pt-8 pb-24 lg:px-0 lg:pt-0"
        >
          {/* Header con back + título */}
          <header className="mb-5 sm:mb-6">
            {/* Dentro del shell, el "Volver" lo reemplaza el breadcrumb
                (ADR-2026-08-03-breadcrumbs-asistente-plantillas-estudio): dice lo
                mismo —de dónde venís— y además dónde estás. Se cambian entre sí en
                el mismo breakpoint, así que nunca se ven los dos.

                Ojo: el breadcrumb ocupa el lugar del "Volver", que era justo lo que
                empujaba el título hacia abajo. En esta pantalla el título vuelve a
                quedar ~30px más abajo que el de `/inicio` — es el precio de tener
                breadcrumb, y no lo tienen las secciones que sí están en el sidebar. */}
            <EncabezadoPantalla
              variante="seccion"
              migas={[
                // `rutaHomeV2` y no `/interfaz-v2` a secas: la home tiene una ruta
                // distinta según el shell que monte esta pantalla, y es el mismo
                // destino al que ya cae el back sin historial.
                { label: 'Inicio', onClick: () => navigate(rutaHomeV2) },
                { label: 'Plantillas' },
              ]}
            />
            <button
              type="button"
              onClick={goBack}
              className="hidden sm:inline-flex lg:hidden items-center gap-1 text-brand-primary text-[14px] mb-4 hover:opacity-75 transition-opacity"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver
            </button>
            <h1 className="text-[24px] sm:text-[32px] font-semibold text-brand-black/90 leading-tight">
              Plantillas
            </h1>
          </header>

          {/* Row horizontal de chips (categorías). Single-select, y el activo se
              apaga tocándolo de nuevo — sin él, todas las plantillas. Para sticky usamos un
              `IntersectionObserver` + position swap (ver useEffect
              arriba): cuando el sentinel sale del viewport por arriba,
              el bar pasa a `position: fixed top: 0` y mostramos un
              spacer del mismo alto para que el grid no salte. */}
          {categories.length > 0 && (
            <>
              <div ref={sentinelRef} aria-hidden="true" />
              <div
                ref={chipsBarRef}
                className={
                  // Estado natural: fondo sólido y negative margins para sangrar
                  // borde a borde. El borde inferior se fue
                  // (ADR-2026-08-03-filtros-plantillas-sin-chip-todas): partía la
                  // pantalla en dos y los chips ya se separan de la grilla por su
                  // propio aire.
                  // Estado fixed: fondo en degradé top→bottom (sólido
                  // arriba, transparente abajo) — sin borde duro y sin
                  // negative margins (porque pisarían el sidebar). El
                  // stop al ~70% deja la transición "repentina" como
                  // pidió el user en vez de un fade suave.
                  isStuck
                    ? 'fixed right-0 z-40 pb-3 sm:pb-4'
                    : // `lg:`: con la página ya sin fondo propio, este casi-blanco
                      // quedaba como una franja clara justo debajo del título, y las
                      // sangrías laterales sobresalían de la columna del shell, que
                      // ya no tiene padding que compensarlas.
                      'bg-surface-mobile sm:bg-surface-content lg:bg-transparent -mx-3 sm:-mx-5 lg:mx-0 relative mb-3 sm:mb-4'
                }
                style={
                  isStuck
                    ? {
                        left: isMobileView ? 0 : sidebarWidth,
                        // Pegada al tope de lo que se ve, que NO es siempre el tope
                        // de la ventana: cada shell tiene su chrome fijo arriba.
                        //
                        //  • Escritorio: la barra de créditos, que es `sticky top-0`
                        //    dentro del scroller del shell. El shell publica dónde
                        //    termina en `--shell-barra-creditos` (vale 0 cuando no
                        //    está) — la misma variable que usan los paneles de modo
                        //    (ADR-2026-08-03-paneles-de-modo-arrancan-bajo-la-barra).
                        //  • Mobile: el header flotante de `DashboardLayout`, que
                        //    mide 3.5rem más el inset de la status bar en PWA. Es el
                        //    mismo `calc()` con el que ese layout separa su contenido
                        //    (`mainTopPadding`).
                        //
                        // Con `top: 0` los chips se metían debajo del header (que es
                        // `z-50`) y en escritorio tapaban la barra de créditos: en los
                        // dos casos el filtro dejaba de verse justo al scrollear, que
                        // es cuando tiene que estar.
                        // En MOBILE la franja arranca en 0 y baja los chips con
                        // `paddingTop`, en vez de arrancar debajo del header: el
                        // header de `DashboardLayout` es transparente —sólo flota el
                        // botón de volver—, así que con la barra empezando a los 56px
                        // quedaba una banda por la que se veían pasar las plantillas
                        // por detrás del botón. Los chips siguen exactamente donde
                        // estaban; lo que cambia es hasta dónde llega el fondo.
                        top: isMobileView ? 0 : 'var(--shell-barra-creditos, 0px)',
                        paddingTop: isMobileView
                          ? 'calc(env(safe-area-inset-top) + 3.5rem)'
                          : undefined,
                        // Pegado arriba el degradé tapa lo que pasa por debajo, así
                        // que tiene que ser del color del lienzo. Son tres lienzos
                        // distintos: blanco en mobile, `#FBFBFD` en el shell viejo y
                        // el gris del shell nuevo — de ahí la variable, que
                        // `.shell-seccion` pisa (ADR-2026-08-03-titulos-secciones-igual-que-inicio).
                        background: isMobileView
                          ? // Mismo gris del lienzo de mobile (`--app-mobile-bg`) que
                            // pinta el shell y el overlay de arriba: en blanco, la
                            // franja pegada se leía como un bloque más claro sobre la
                            // grilla.
                            'linear-gradient(to bottom, var(--app-mobile-bg, #F9F9F9) 0%, var(--app-mobile-bg, #F9F9F9) 70%, rgba(249,249,249,0) 100%)'
                          // El último stop va como color explícito con alfa 0 y no
                          // como `transparent`: `transparent` es rgba(0,0,0,0) y el
                          // degradé se ensucia de gris en el medio. De ahí la
                          // segunda variable.
                          : 'linear-gradient(to bottom, var(--plantillas-sticky-bg, #FBFBFD) 0%, var(--plantillas-sticky-bg, #FBFBFD) 70%, var(--plantillas-sticky-bg-0, rgba(251,251,253,0)) 100%)',
                      }
                    : undefined
                }
              >
                {/* Pegada, el wrapper se calza contra la columna MEDIDA en vez de
                    rehacerla con `max-w-content mx-auto`: así el primer chip arranca
                    exactamente donde arranca la primera imagen, y las flechas —que se
                    posicionan contra este wrapper— caen sobre los bordes de la grilla
                    y no sobre los de la franja. */}
                <div
                  className="relative"
                  style={
                    isStuck && columna
                      ? {
                          marginLeft:
                            columna.izquierda - (isMobileView ? 0 : sidebarWidth),
                          marginRight: Math.max(0, columna.ancho - columna.derecha),
                        }
                      : undefined
                  }
                >
                  {/* Flecha izquierda — aparece solo si el row está scrolleado
                      hacia adelante (hay categorías ocultas a la izquierda).
                      Desktop only; en mobile el user swipea. El wrapper
                      posiciona y centra (flex), NO el botón: si el botón usara
                      `-translate-y-1/2`, el `:active { transform: scale(.95) }`
                      global (index.css) pisaría ese translate y lo tiraría hacia
                      abajo al presionar (y a veces cancelaba el click). El
                      contenedor es `pointer-events-none` para no tapar los chips
                      de abajo; el botón re-habilita clicks solo cuando se ve. */}
                  <div className="hidden sm:flex absolute left-1 inset-y-0 z-10 items-center pointer-events-none">
                    <button
                      type="button"
                      aria-label="Ver categorías anteriores"
                      onClick={() => scrollCategories(-1)}
                      className={`w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/[0.06] text-brand-black/70 hover:text-brand-black transition-opacity duration-200 ${
                        canScrollLeft
                          ? 'opacity-100 pointer-events-auto'
                          : 'opacity-0 pointer-events-none'
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                  </div>
                  <div
                    ref={categoryScrollRef}
                    onScroll={measureCategoryArrows}
                    className={`flex gap-2 overflow-x-auto scrollbar-hide py-3 sm:py-4 ${
                      // Suelto, el padding compensa la sangría de la barra para que
                      // el primer chip caiga sobre el borde del contenido; dentro
                      // del shell no hay sangría que compensar (`lg:mx-0`), así que
                      // tampoco padding, y el chip queda alineado con el título.
                      // Pegada y con la columna medida, el aire lo da el wrapper:
                      // un padding acá volvería a correr el primer chip. El
                      // `px-5 sm:px-8` queda de respaldo para el primer frame, antes
                      // de que la medición exista.
                      isStuck
                        ? columna
                          ? 'px-0'
                          : 'px-5 sm:px-8'
                        : 'px-3 sm:px-5 lg:px-0'
                    }`}
                    style={{
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      WebkitOverflowScrolling: 'touch',
                    }}
                  >
                  {/* Sólo las categorías: el chip "Todas las plantillas" se fue
                      (ADR-2026-08-03-filtros-plantillas-sin-chip-todas). Ocupaba el
                      lugar más valioso de una fila que scrollea para ofrecer el
                      estado en el que ya estás al entrar, y ahora se vuelve a él
                      tocando el chip activo. Con él se fue el divisor vertical que
                      lo separaba del resto, que ya no separa nada. */}
                  {categories.map((c) => {
                    const active = selectedCategoryId === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => selectCategory(c.id)}
                        aria-pressed={active}
                        // Borde igual al de la sidebar (`border-black/[0.06]`): el
                        // que tenía —`border-brand-black/80`— no generaba regla
                        // (token sin `<alpha-value>`) y caía al gris por defecto de
                        // Tailwind, que no es el de ninguna otra superficie de la app.
                        className={`shrink-0 px-4 py-2 rounded-full text-[14px] font-medium transition-colors border ${
                          active
                            ? 'border-brand-primary text-brand-primary bg-[#EBEFFE]'
                            : 'border-black/[0.06] bg-white hover:bg-black/[0.03]'
                        }`}
                      >
                        {c.title}
                      </button>
                    );
                  })}
                  </div>
                  {/* Flecha derecha — aparece solo si falta contenido por
                      scrollear (hay categorías ocultas a la derecha). Mismo
                      patrón wrapper-flex que la izquierda (ver comentario). */}
                  <div className="hidden sm:flex absolute right-1 inset-y-0 z-10 items-center pointer-events-none">
                    <button
                      type="button"
                      aria-label="Ver más categorías"
                      onClick={() => scrollCategories(1)}
                      className={`w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/[0.06] text-brand-black/70 hover:text-brand-black transition-opacity duration-200 ${
                        canScrollRight
                          ? 'opacity-100 pointer-events-auto'
                          : 'opacity-0 pointer-events-none'
                      }`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Spacer cuando está fijo — mantiene el flow del grid
                  para que no salte hacia arriba al desconectarse el
                  bar del documento. */}
              {isStuck && (
                <div
                  aria-hidden="true"
                  style={{ height: chipsHeight, marginBottom: 12 }}
                />
              )}
            </>
          )}

          {/* Grilla masonry — CSS columns. Cada card se renderiza con su
              aspect natural; los `break-inside-avoid` evitan que el
              navegador corte una card entre columnas. */}
          {filteredPresets.length === 0 ? (
            <div className="text-[14px] text-[#3C3C43]/60">
              {categories.length === 0
                ? 'Todavía no hay plantillas disponibles para tu rubro.'
                : 'No hay plantillas para los filtros seleccionados.'}
            </div>
          ) : (
            <div
              // `key` ligado a la categoría → React remonta el div
              // cuando cambia el filtro, lo que dispara la animación
              // `category-swap` (fade + slide horizontal sutil desde
              // la derecha) en cada swap.
              key={selectedCategoryId}
              className="columns-2 sm:columns-3 lg:columns-4 gap-3 sm:gap-4 animate-category-swap"
            >
              {filteredPresets.map((p, i) => (
                <MasonryCard
                  key={p.id}
                  preset={p}
                  // Patrón cíclico de aspect ratios para que aún cuando
                  // todas las imágenes originales sean 3:4, la grilla
                  // se vea variada estilo galería. Los cards se croppean
                  // con `object-cover`, así no se distorsiona la imagen.
                  aspectClass={ASPECT_CYCLE[i % ASPECT_CYCLE.length]}
                  onClick={() => openPreset(p)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default InterfazV2Plantillas;
